import { Lock, Mail, MessageCircle, Phone, Send } from "lucide-react";
import { CoBrokerScorePill } from "@/components/CoBrokerScorePill";

/**
 * CoBrokerContactCard — the "handoff" moment on a match.
 *
 * Locked (Pending): shows a Lock icon + "Advance to Inquiry to unlock
 *   contact details." — matches the server-side rule (contact fields
 *   are omitted from the API payload at Pending, not just hidden here).
 * Revealed (Inquiry, Negotiation, Closed): counterpart name + verified
 *   badge + co-broker score + phone (tel:) + email (mailto:) + Viber
 *   deep-link + WhatsApp deep-link. No in-app messaging — this card is
 *   purely a handoff.
 *
 * Compact variant fits inside a MatchesPage card; the default full
 * variant is used on Pipeline card detail modals.
 */
export function CoBrokerContactCard({ match, compact = false }) {
  const revealed = match.status !== "Pending";
  const cp = match.counterpart || {};
  const phone = cp.phone;
  const email = cp.email;

  if (!revealed) {
    return (
      <div
        className="mt-3 flex items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-2"
        data-testid={`contact-card-locked-${match.id}`}
      >
        <span className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white text-gray-400">
          <Lock className="h-3 w-3" aria-hidden="true" />
        </span>
        <p className="text-[11px] font-medium leading-tight text-gray-500">
          Advance to Inquiry to unlock the co-broker&apos;s phone and email.
        </p>
      </div>
    );
  }

  // Normalize a stored PH phone to WhatsApp (digits only) and Viber
  // (E.164 with the leading +) formats. Empty string if not stored.
  const digitsOnly = phone ? phone.replace(/[^\d]/g, "") : "";
  const viberNumber = phone
    ? phone.startsWith("+")
      ? phone
      : "+" + digitsOnly
    : "";
  const waHref = digitsOnly
    ? `https://wa.me/${digitsOnly}`
    : null;
  const viberHref = viberNumber
    ? `viber://chat?number=${encodeURIComponent(viberNumber)}`
    : null;
  const mailHref = email ? `mailto:${email}` : null;
  const telHref = phone ? `tel:${phone}` : null;

  return (
    <div
      className={
        "mt-3 rounded-2xl border border-[#35622F]/25 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)] " +
        (compact ? "text-xs" : "text-sm")
      }
      data-testid={`contact-card-revealed-${match.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#35622F]">
            Co-broker contact
          </div>
          <div
            className="mt-0.5 truncate text-sm font-semibold text-gray-900"
            data-testid={`contact-card-name-${match.id}`}
          >
            {cp.full_name}
          </div>
          <div className="mt-0.5 text-[11px] text-gray-500">
            PRC {cp.prc_license_number || "—"}
          </div>
        </div>
        <CoBrokerScorePill score={match.counterpart_score} />
      </div>

      <div className="mt-3 flex flex-col gap-1.5">
        {phone && (
          <a
            href={telHref}
            className="mb-focus-ring inline-flex h-8 items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-800 hover:bg-gray-50"
            data-testid={`contact-phone-${match.id}`}
          >
            <Phone className="h-3 w-3 text-gray-500" />
            <span className="tabular-nums">{phone}</span>
          </a>
        )}
        {mailHref && (
          <a
            href={mailHref}
            className="mb-focus-ring inline-flex h-8 items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-800 hover:bg-gray-50"
            data-testid={`contact-mailto-${match.id}`}
          >
            <Mail className="h-3 w-3 text-gray-500" />
            <span className="truncate">{email}</span>
          </a>
        )}
      </div>

      {(waHref || viberHref) && (
        <div className="mt-2 flex gap-1.5">
          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              className="mb-focus-ring inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#25D366] px-2 text-[11px] font-semibold text-white hover:bg-[#1FB255]"
              data-testid={`contact-whatsapp-${match.id}`}
            >
              <MessageCircle className="h-3 w-3" />
              WhatsApp
            </a>
          )}
          {viberHref && (
            <a
              href={viberHref}
              className="mb-focus-ring inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#7360F2] px-2 text-[11px] font-semibold text-white hover:bg-[#5C4CD1]"
              data-testid={`contact-viber-${match.id}`}
            >
              <Send className="h-3 w-3" />
              Viber
            </a>
          )}
        </div>
      )}
    </div>
  );
}
